import ConfirmDialog, {
  ConfirmDialogContent,
} from '@/features/shared/Dialogs/ConfirmDialog/ConfirmDialog';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type ConfirmDialogTrigger = (confirmProps: Omit<ConfirmDialogContent, 'isOpen'>) => void;

type DialogsContextValue = {
  confirm: ConfirmDialogTrigger;
} | null;

const DialogsContext = createContext<DialogsContextValue>(null);

export const DialogsProvider = ({ children }: PropsWithChildren<any>) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState<
    Omit<ConfirmDialogTrigger, 'isOpen'>
  >({});

  const confirmDialog = useCallback<ConfirmDialogTrigger>((confirmProps) => {
    setConfirmDialogOpen(true);
    setConfirmDialogProps({
      ...confirmProps,
      reject: () => {
        if (confirmProps?.reject) {
          confirmProps?.reject();
        }
        setConfirmDialogOpen(false);
      },
      accept: () => {
        if (confirmProps?.accept) {
          confirmProps?.accept();
        }
        setConfirmDialogOpen(false);
      },
    });
  }, []);

  const contentValue = useMemo<DialogsContextValue>(
    () => ({
      confirm: confirmDialog,
    }),
    [confirmDialog],
  );

  return (
    <DialogsContext.Provider value={contentValue}>
      <>
        {children}
        <ConfirmDialog {...confirmDialogProps} isOpen={confirmDialogOpen} />
      </>
    </DialogsContext.Provider>
  );
};

export const useDialogs = () => {
  const context = useContext(DialogsContext);

  if (!context) {
    throw new Error('You need the DialogsProvider');
  }
  return context;
};
